import { useState } from 'react';
import { useBusinessData } from '../../authentication_components/businessDataContext';
import { useBusinessSubscription } from '../../authentication_components/businessSubscriptionContext';
import { Search, AlertTriangle, Building, User, CreditCard, CheckCircle, XCircle, AlertCircle as AlertIcon, DollarSign } from 'lucide-react';

// Import the new Dialog Component
import SubscriptionTrialDialog from './business_subscriptions_subcomponents/Subscription_trial_dialog'; 

export default function ManageSubscriptions() {
  const [registryNumber, setRegistryNumber] = useState('');
  const [businessData, setBusinessData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const { getBusinessByRegistryNumber } = useBusinessData();
  const { getSubscriptionByRegistryNumber, formatSubscriptionData, getBillingHistory } = useBusinessSubscription();

  const handleSearch = async (e) => {
    // If e is present (form submit), prevent default. 
    // If called programmatically (refreshing data), e might be null/undefined.
    if (e) e.preventDefault();
    
    // Allow refreshing using the state's registryNumber even if e is missing
    const targetRegistry = registryNumber.trim();

    if (!targetRegistry) return;

    setIsSearching(true);
    setSearchError(null);
    // Note: We don't clear businessData immediately on refresh to avoid UI flash, 
    // but for a new search we might want to.
    
    try {
      // Step 1: Find the business by its registry number
      const business = await getBusinessByRegistryNumber(targetRegistry);
      setBusinessData(business);

      // Step 2: Fetch subscription and billing history concurrently
      await Promise.all([
        (async () => {
          try {
            const subscription = await getSubscriptionByRegistryNumber(business.business_registry_number);
            setSubscriptionData(formatSubscriptionData(subscription));
          } catch (subError) {
            if (!subError.message.toLowerCase().includes('not found')) {
              console.error("Subscription fetch error:", subError);
            }
          }
        })(),
        (async () => {
          try {
            const historyData = await getBillingHistory(business.business_registry_number);
            setBillingHistory(historyData.history || []);
          } catch (historyError) {
             if (!historyError.message.toLowerCase().includes('not found')) {
               console.error("Billing history fetch error:", historyError);
             }
          }
        })(),
      ]);

    } catch (err) {
      setSearchError(err.message || 'An unexpected error occurred.');
      setBusinessData(null);
    } finally {
      setIsSearching(false);
    }
  };
  
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString || dateTimeString === 'N/A') return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('es-PR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-PR', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatCurrency = (amount, currency) => {
    if (typeof amount !== 'number' || !currency) return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: currency.toUpperCase(),
    }).format(amount / 100); 
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'canceled':
      case 'ended':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'succeeded':
        return 'text-green-600 bg-green-100';
      case 'canceled':
      case 'ended':
        return 'text-red-600 bg-red-100';
      case 'past_due':
      case 'incomplete':
        return 'text-orange-600 bg-orange-100';
      case 'trialing':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'premium':
      case 'corporacion':
        return 'text-purple-600 bg-purple-100';
      case 'standard':
      case 'compania':
        return 'text-indigo-600 bg-indigo-100';
      case 'basic':
      case 'negocio':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
      {/* Sub-header */}
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <form onSubmit={handleSearch} className="flex gap-4 max-w-2xl">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-orange-400" />
              </div>
              <input
                type="text"
                placeholder="Ingrese el número de registro del negocio..."
                value={registryNumber}
                onChange={(e) => setRegistryNumber(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200"
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !registryNumber.trim()}
              className="px-6 py-2 bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSearching ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Buscando...
                </div>
              ) : (
                <div className="flex items-center">
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </div>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {searchError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  NEGOCIO NO ENCONTRADO
                </h3>
                <p className="text-red-600 mt-1">
                  {searchError.includes('not found') 
                    ? `El número de registro "${registryNumber}" no se encontró en la base de datos.`
                    : `Error: ${searchError}`
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {businessData && (
          <div className="space-y-8">
            {/* Business Details Header */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  <Building className="h-6 w-6 text-orange-500 mr-3" />
                  {businessData.business_name}
                </h2>
                
                {/* 
                   INTEGRATED DIALOG HERE 
                   We pass a callback to handleSearch() to refresh data after trial is given/cancelled
                */}
                <SubscriptionTrialDialog 
                    businessRegistryNumber={businessData.business_registry_number}
                    businessName={businessData.business_name}
                    currentTier={subscriptionData?.tier}
                    onSuccess={() => handleSearch(null)} 
                />
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-orange-500 mr-3" />
                  <span className="text-gray-700 font-medium">{businessData.business_owner}</span>
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <CreditCard className="h-6 w-6 text-orange-500 mr-3" />
                Detalles de Suscripción
              </h3>
              
              {subscriptionData ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Nivel de Suscripción Actual</label>
                      <div className="mt-1">
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getTierColor(subscriptionData.tier)}`}>
                          {subscriptionData.tier}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Estado de Suscripción</label>
                      <div className="mt-1 flex items-center">
                        {getStatusIcon(subscriptionData.status)}
                        <span className={`ml-2 inline-flex px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(subscriptionData.status)}`}>
                          {subscriptionData.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Método de Pago</label>
                      <p className="mt-1 text-gray-800 capitalize">{subscriptionData.provider || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                     <div>
                      <label className="text-sm font-medium text-gray-600">Fecha de Suscripción</label>
                      <p className="mt-1 text-gray-800">{formatDateTime(subscriptionData.created_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Fecha de Cancelación</label>
                      <p className="mt-1 text-gray-800">{formatDateTime(subscriptionData.canceledAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Inicio del Período Actual</label>
                      <p className="mt-1 text-gray-800">{formatDateTime(subscriptionData.periodStart)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Próxima Renovación / Fin del Período</label>
                      <p className="mt-1 text-gray-800 font-bold text-orange-600">{formatDateTime(subscriptionData.periodEnd)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <XCircle className="h-16 w-16 text-orange-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-700 mb-2">Sin Suscripción Activa</h3>
                  <p className="text-gray-500 max-w-md mx-auto">Este negocio no tiene una suscripción registrada.</p>
                </div>
              )}
            </div>

            {/* Billing History Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-orange-200">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                  <DollarSign className="h-6 w-6 text-orange-500 mr-3" />
                  Historial de Facturación ({billingHistory.length} registros)
                </h3>
              </div>

              {billingHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-orange-50 to-orange-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Monto</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Fecha</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Hora</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Proveedor</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nivel</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-100">
                      {billingHistory.map((record) => (
                        <tr key={record.id} className="hover:bg-orange-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-semibold text-green-600">
                            {formatCurrency(record.amount, record.currency)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{formatDate(record.transaction_date)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{formatTime(record.transaction_date)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700 capitalize">{record.provider}</td>
                          <td className="px-6 py-4 text-sm">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTierColor(record.tier)}`}>
                              {record.tier}
                            </span>
                          </td>
                           <td className="px-6 py-4 text-sm">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(record.status)}`}>
                              {record.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <DollarSign className="h-12 w-12 text-orange-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Sin Historial de Facturación</h3>
                  <p className="text-gray-500">Este negocio no tiene transacciones registradas.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Initial State */}
        {!businessData && !searchError && !isSearching && (
          <div className="text-center py-16">
            <CreditCard className="h-16 w-16 text-orange-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">Gestión de Suscripciones</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Ingrese el número de registro de un negocio para ver su información de suscripción y su historial de facturación.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}